import {
  MAX_RECURRING_OCCURRENCES,
  RECURRING_NOT_CONFIRMED,
  type TripCreateRecurringResponse,
} from "@mtform/shared";

// =============================================================================
// Recurrence date generation
//
// Pure date math for the "repeat this trip" picker. Given a start date,
// a set of weekdays, and a number of weeks, produce the list of
// YYYY-MM-DD dates to book.
//
// All arithmetic is done on UTC-anchored dates (YYYY-MM-DD parsed to
// UTC midnight, day stepping via setUTCDate). That sidesteps daylight-
// saving-time shifts entirely — adding "7 days" never lands on the
// wrong calendar day the way local-time Date math can across a DST
// boundary. The output is always a plain calendar date string, which
// is exactly what the trip payload's `transportDate` field wants.
// =============================================================================

/** Weekday indices, Sunday = 0 through Saturday = 6 — matches
 *  JavaScript's `Date.getUTCDay()`. */
export const WEEKDAY_LABELS: ReadonlyArray<{ value: number; short: string; long: string }> = [
  { value: 1, short: "Mon", long: "Monday" },
  { value: 2, short: "Tue", long: "Tuesday" },
  { value: 3, short: "Wed", long: "Wednesday" },
  { value: 4, short: "Thu", long: "Thursday" },
  { value: 5, short: "Fri", long: "Friday" },
  { value: 6, short: "Sat", long: "Saturday" },
  { value: 0, short: "Sun", long: "Sunday" },
];

export interface GenerateRecurringDatesInput {
  /** Anchor / earliest date, YYYY-MM-DD. Occurrences are generated from
   *  this date forward (inclusive when it falls on a selected weekday). */
  startIso: string;
  /** Selected weekday indices (0 = Sun .. 6 = Sat). */
  weekdays: number[];
  /** How many calendar weeks the pattern spans, starting from the week
   *  of `startIso`. 1 = just the start week. */
  weeks: number;
}

/** Parse "YYYY-MM-DD" → a UTC-midnight Date. Returns null on garbage so
 *  callers can fail soft. */
function parseIsoUtc(iso: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/u.test(iso)) return null;
  const d = new Date(`${iso}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function toIso(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/**
 * Generate the recurring dates. Walks day-by-day from `startIso` for
 * `weeks * 7` days, collecting any day whose weekday is in the selected
 * set. Results are sorted ascending, de-duplicated, and capped at
 * MAX_RECURRING_OCCURRENCES (the same ceiling the server enforces).
 *
 * Returns an empty array when the inputs are incomplete (no start, no
 * weekdays, or weeks < 1) — the UI treats that as "nothing to book yet".
 */
export function generateRecurringDates({
  startIso,
  weekdays,
  weeks,
}: GenerateRecurringDatesInput): string[] {
  const start = parseIsoUtc(startIso);
  if (!start || weekdays.length === 0 || weeks < 1) return [];

  const wanted = new Set(weekdays);
  const totalDays = weeks * 7;
  const out: string[] = [];

  const cursor = new Date(start);
  for (let i = 0; i < totalDays && out.length < MAX_RECURRING_OCCURRENCES; i += 1) {
    if (wanted.has(cursor.getUTCDay())) {
      out.push(toIso(cursor));
    }
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }

  return out;
}

/** The pattern a series' dates were built from, as the form remembers it:
 *  a restored draft whose list was built from the pattern it shows keeps its
 *  list, dates taken out and all. */
export function repeatPatternKey(startIso: string, weekdays: readonly number[], weeks: number): string {
  return JSON.stringify([startIso, [...weekdays].sort(), weeks]);
}

/** Format a YYYY-MM-DD date for display in the recurrence preview —
 *  "Mon, Jun 2". UTC-anchored so it never drifts a day. */
export function formatRecurringDate(iso: string): string {
  const d = parseIsoUtc(iso);
  if (!d) return iso;
  return d.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

/** The series after a re-book: each re-sent date's row takes its new
 *  outcome, and every other row stays as it was, so a date dispatch never
 *  confirmed is still there to check. */
export function afterRebook(
  previous: TripCreateRecurringResponse,
  retried: TripCreateRecurringResponse,
): TripCreateRecurringResponse {
  const results = previous.results.map(
    (r) => retried.results.find((n) => n.date === r.date) ?? r,
  );
  const createdCount = results.filter((r) => r.status === "created").length;
  return { results, createdCount, failedCount: results.length - createdCount };
}

/** Dates sent without a clear answer, the ones the booker has checked, and
 *  what the portal said about any of them. */
export interface UnconfirmedDates {
  dates: string[];
  checked: string[];
  notes?: Record<string, string>;
}

/** After sending these dates: until a clear answer comes back they may book,
 *  on top of any held already, and any earlier check of them, or note on
 *  them, is out of date. */
export function holdDates(
  held: UnconfirmedDates,
  sent: readonly string[],
): UnconfirmedDates {
  return {
    dates: [...new Set([...held.dates, ...sent])],
    checked: held.checked.filter((d) => !sent.includes(d)),
    notes: Object.fromEntries(
      Object.entries(held.notes ?? {}).filter(([d]) => !sent.includes(d)),
    ),
  };
}

/** The result with these dates marked as not confirmed: a re-book of them got
 *  no clear answer, so they may have booked. */
export function markUnconfirmed(
  result: TripCreateRecurringResponse,
  dates: readonly string[],
): TripCreateRecurringResponse {
  return {
    ...result,
    results: result.results.map((r) =>
      dates.includes(r.date) ? { ...r, unconfirmed: true, error: RECURRING_NOT_CONFIRMED } : r,
    ),
  };
}
